import { Suspense, useMemo, useState } from "react";
import Breadcrumb from "../../../../components/ui/materialui/breadcrumb";
import { Box, Paper } from "@mui/material";
import CustomAccordion from "../../../../components/ui/materialui/customAccordion";
import { FiltroBusquedaCorreccionMonetaria } from "../models/filtroBusqueda";
import { FormularioBusquedaCorreccionMonetaria } from "../components/formularioBusquedaCorreccionMonetaria";
import UseColumns from "../utils/columns";
import Tabla from '../../../../components/ui/materialui/serverSideTable';
import { useDataFetch } from '../../../../hooks/useDataFetch2';
import TableHeader from "../../../../components/ui/materialui/tableHeader";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { exportExcel } from "../../../../utils/exportExcel";
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { RowCorreccionMonetaria } from "../models/rowCorreccionMonetaria";
import { deleteMesAnterior } from "../services/correccionMonetariaService";
import { useToast } from "../../../../hooks/useToast";
import { PropsModalEdit } from "../interfaces/PropsModalEdit.interface";
import ModalAddEditFactor from "../components/modalAddEditFactor";
import { PermissionGuard } from "../../../../components/auth/ProtectedRoute";
import { TipoFuncionalidad } from "../../../../utils/enums";

const breadcrumbItems = [
    { label: 'Inicio', url: '/' },
    { label: 'Maestros', url: `/maestros` },
    { label: 'Corrección monetaria', active: true },
]

const urlTabla = `siscred-api-maestros/v1/correccionmonetaria/listar`;

export default function CorreccionMonetaria(){

    const { addToast } = useToast();
    const anioPasado = new Date().getFullYear() - 1;

    const [valueForm, setValueForm] = useState<FiltroBusquedaCorreccionMonetaria>();
    const [propsModalEdit, setPropsModalEdit] = useState<PropsModalEdit>({
        show: false,
        onHide: () => {},
        anno: 0,
        mesComercial: 0,
        formularioDefecto: null,
        isUpdate: null,
        onComplete: () => {}
    });


    const params = useMemo(() => {
        const p = new URLSearchParams();
        p.set("anno", anioPasado.toString());
        p.set("mesComercial", '12');
        p.set("hijosActivos", 'true');
        setValueForm({ anioComercial: anioPasado, mesComercial: 'Diciembre', valoresActivos: true }); // Por defecto
        return p;
    }, []);

    const controlTabla = useDataFetch(
        urlTabla,
        params,
        true,
    )

    const onSubmit = async (e: FiltroBusquedaCorreccionMonetaria) => {
        setValueForm(e);

        controlTabla.manejarCambiosFiltros('anno', e.anioComercial.toString());
        controlTabla.manejarCambiosFiltros('mesComercial', '12');
        controlTabla.manejarCambiosFiltros('hijosActivos', e.valoresActivos ? 'true' : 'false');
    
        await controlTabla.fetchData();
    };

    const onUpdate = (row: RowCorreccionMonetaria) => {
        setPropsModalEdit({
            show: true,
            onHide: onHide,
            anno: row.anno, 
            mesComercial: 12,
            formularioDefecto: {
                annoComercial: row.anno,
                mesComercial: 12,
                mesCalendario: row.numero,
                factor: row.factor.toString()
            },
            isUpdate: true,
            onComplete: () => controlTabla.fetchData()
        });
    };
    
    const onCreate = () => {
        setPropsModalEdit({
            show: true,
            onHide: onHide,
            anno: valueForm!.anioComercial, 
            mesComercial: 12,
            isUpdate: false,
            onComplete: () => controlTabla.fetchData()
        });
        
    };

    const onDelete = async (row: RowCorreccionMonetaria) => {
        const resp = await deleteMesAnterior(row);
        
        if( resp.status === 200 ){
            showToast('Corrección Monetaria', 'Success', 'Factor eliminado correctamente.');
        }else if( resp.status === 409 ){
            showToast('Corrección Monetaria', 'Warning', resp?.response?.data);
        }else {
            showToast('Corrección Monetaria', 'Danger', 'Ha ocurrido un error.');
        }
        await controlTabla.fetchData();

    };

    const onHide = () => {
        setPropsModalEdit({
            show: false,
            onHide: () => {},
            anno: 0,
            mesComercial: 0,
            formularioDefecto: null,
            isUpdate: null,
            onComplete: () => controlTabla.fetchData(),
        });
    }

    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
          mostrar: true,
          titulo: title,
          tipo,
          mensaje,
        });
    }

    const { COLUMNS } = UseColumns({onUpdate, onDelete})
    const columns = useMemo(() => COLUMNS, [controlTabla, onUpdate, onDelete])

    return (
        <>
          <Suspense>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <CustomAccordion 
                        title='Administrar corrección monetaria'
                        expandedDefault
                    >
                        <FormularioBusquedaCorreccionMonetaria onSubmit={onSubmit} />
                    </CustomAccordion>
                    <Tabla          
                        control={controlTabla}
                        columns={columns}
                        url={urlTabla}
                        titulo={
                            <TableHeader
                            title=""
                            control={controlTabla}
                            button={
                                <PermissionGuard permissions={[TipoFuncionalidad.Maestros_editar]}>
                                <MButton
                                    type="button"
                                    variant="contained"
                                    onClick={onCreate}
                                    startIcon={<AddTwoToneIcon />}
                                >
                                    Nuevo
                                </MButton>
                                </PermissionGuard>
                            }
                            buttonExport={
                                <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                <MButton
                                type="button"
                                variant="outlined"
                                onClick={() => exportExcel(controlTabla.data, columns, "TASA")}
                                startIcon={<SummarizeTwoToneIcon />}
                                >
                                    Exportar
                                </MButton>
                                </PermissionGuard>
                            }
                            />
                        }
                    />
                </Paper>
            </Box>
          </Suspense>
          { propsModalEdit.show && (
            <ModalAddEditFactor 
                show={propsModalEdit.show}
                onHide={propsModalEdit.onHide}
                anno={propsModalEdit.anno}
                mesComercial={propsModalEdit.mesComercial}
                formularioDefecto={propsModalEdit.formularioDefecto}
                isUpdate={propsModalEdit.isUpdate}
                onComplete={propsModalEdit.onComplete}
            />
          ) }
        </>
    );
}
